import { useParams, NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { LuClock3 } from 'react-icons/lu';
import { projectsMeta } from '../helpers/projectsList';
import PageSEO from '../components/PageSEO';
import './../style/project.css';
import '../components/button/style.css';

const ArrowLeft = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);

const ArrowRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const ExternalIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15,3 21,3 21,9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const GitHubIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2z" />
  </svg>
);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.1,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

const Project = () => {
  const { id } = useParams();
  const { t } = useTranslation('projects');
  const idx = Number(id);

  const translatedItems = t('items', { returnObjects: true });
  const projects = Array.isArray(translatedItems)
    ? translatedItems.map((item, i) => ({ ...item, ...projectsMeta[i] }))
    : [];

  const project = !isNaN(idx) ? projects[idx] : undefined;
  const num = String(idx + 1).padStart(2, '0');
  const prevProject = project && idx > 0 ? projects[idx - 1] : null;
  const nextProject = project && idx < projects.length - 1 ? projects[idx + 1] : null;

  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    setImgLoaded(false);
  }, [id]);

  if (!project) {
    return (
      <main className="proj-detail">
        <PageSEO title={`${t('notFound')} | PixelCraft`} path={`/project/${idx}`} />
        <div className="space"></div>
        <div className="container">
          <NavLink to="/projects" className="proj-detail__back">
            <ArrowLeft /> {t('allProjects')}
          </NavLink>
          <p className="proj-detail__not-found">{t('notFound')}</p>
        </div>
      </main>
    );
  }

  const seoDesc = project.description
    ? project.description.length > 160
      ? project.description.slice(0, 157) + '…'
      : project.description
    : '';

  const skills = project.skills
    ? project.skills.split(',').map((s) => s.trim())
    : [];
  const isPublished = project.isPublished !== false;
  const externalLink = isPublished ? (project.websiteLink || project.gitHubLink) : null;
  const isGitHub = !project.websiteLink && project.gitHubLink;
  const buttonLabel = isGitHub ? t('viewGithub') : t('visitSite');
  const comingSoonText = project.comingSoonMessage || t('comingSoonDefault');

  return (
    <main className="proj-detail">
      <PageSEO
        title={`${project.title} | PixelCraft`}
        description={seoDesc}
        path={`/project/${idx}`}
      />
      <div className="space"></div>
      <div className="container">
        <NavLink to="/projects" className="proj-detail__back">
          <ArrowLeft /> {t('allProjects')}
        </NavLink>

        {/* Hero */}
        <motion.div
          className="proj-detail__hero"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <img
            src={project.imgBig || project.img}
            alt={`${project.title} project preview`}
            className={`proj-detail__hero-img${imgLoaded ? ' loaded' : ''}`}
            onLoad={() => setImgLoaded(true)}
            loading="eager"
            decoding="async"
          />
          <div className="proj-detail__hero-gradient" />
          <div className="proj-detail__hero-content">
            <motion.span
              className="proj-detail__num"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
            >
              {t('projectNum', { num })}
            </motion.span>
            <motion.h1
              className="proj-detail__title"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
            >
              {project.title}
            </motion.h1>
          </div>
        </motion.div>

        {/* Body */}
        <div className="proj-detail__body">
          {skills.length > 0 && (
            <motion.div
              className="proj-detail__skills"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
            >
              {skills.map((skill) => (
                <span key={skill} className="proj-detail__skill">
                  {skill}
                </span>
              ))}
            </motion.div>
          )}

          <motion.div
            className="proj-detail__divider"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
          />

          {project.description && (
            <motion.div
              className="proj-detail__desc-card"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={5}
            >
              <span className="proj-detail__desc-label">{t('aboutProject')}</span>
              <p className="proj-detail__desc-text">{project.description}</p>
            </motion.div>
          )}

          {isPublished && externalLink && (
            <motion.div
              className="proj-detail__actions"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={6}
            >
              <a
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="button"
              >
                {buttonLabel}
                {isGitHub ? <GitHubIcon /> : <ExternalIcon />}
              </a>
            </motion.div>
          )}

          {!isPublished && (
            <motion.div
              className="proj-detail__coming-soon"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={6}
            >
              <LuClock3 className="proj-detail__coming-soon-icon" />
              <p className="proj-detail__coming-soon-text">{comingSoonText}</p>
            </motion.div>
          )}
        </div>

        {/* Prev / Next navigation */}
        <motion.nav
          className="proj-detail__nav"
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={7}
          aria-label="Project navigation"
        >
          {prevProject ? (
            <NavLink
              to={`/project/${idx - 1}`}
              className="proj-detail__nav-link proj-detail__nav-link--prev"
            >
              <ArrowLeft />
              <div className="proj-detail__nav-label">
                <span className="proj-detail__nav-dir">{t('previous')}</span>
                <span className="proj-detail__nav-name">{prevProject.title}</span>
              </div>
            </NavLink>
          ) : (
            <span />
          )}

          {nextProject ? (
            <NavLink
              to={`/project/${idx + 1}`}
              className="proj-detail__nav-link proj-detail__nav-link--next"
            >
              <div className="proj-detail__nav-label">
                <span className="proj-detail__nav-dir">{t('next')}</span>
                <span className="proj-detail__nav-name">{nextProject.title}</span>
              </div>
              <ArrowRight />
            </NavLink>
          ) : (
            <span />
          )}
        </motion.nav>
      </div>
    </main>
  );
};

export default Project;